// headerValidation.js
// Preflight CSV header validation used by FileUploader.jsx.
// NOTE: this module was referenced by FileUploader.jsx but missing from the
// repo (the production build failed on it). Implemented permissively: only
// truly broken header rows fail (`ok: false`); missing recommended headers
// merely produce a `suggestions` string (warn toast in FileUploader).

const RECOMMENDED_HEADERS = [
	"company_name",
	"first_name",
	"last_name",
	"email",
	"address",
	"city",
	"normalized_phone",
];

// Minimal CSV first-line parser (handles quoted fields with commas).
function parseHeaderLine(line, delimiter = ",") {
	const out = [];
	let cur = "";
	let inQuotes = false;
	for (let i = 0; i < line.length; i++) {
		const ch = line[i];
		if (inQuotes) {
			if (ch === '"') {
				if (line[i + 1] === '"') {
					cur += '"';
					i++;
				} else {
					inQuotes = false;
				}
			} else {
				cur += ch;
			}
		} else if (ch === '"') {
			inQuotes = true;
		} else if (ch === delimiter) {
			out.push(cur);
			cur = "";
		} else {
			cur += ch;
		}
	}
	out.push(cur);
	return out.map((h) => h.trim().replace(/^﻿/, ""));
}

function detectDelimiter(line) {
	const candidates = [",", ";", "\t"];
	let best = ",";
	let bestCount = -1;
	for (const d of candidates) {
		const count = line.split(d).length - 1;
		if (count > bestCount) {
			best = d;
			bestCount = count;
		}
	}
	return best;
}

export function headerGuidance() {
	return (
		"Recommended headers (optional, improve matching quality):\n" +
		RECOMMENDED_HEADERS.join(", ")
	);
}

// Returns { ok, problems, suggestions }.
export async function validateHeadersFromFile(file) {
	const head = await file.slice(0, 64 * 1024).text();
	const firstLine = head.split(/\r?\n/, 1)[0] ?? "";

	if (!firstLine.trim()) {
		return {
			ok: false,
			problems: ["The first line of the file is empty — no headers found."],
			suggestions: null,
		};
	}

	const delimiter = detectDelimiter(firstLine);
	const headers = parseHeaderLine(firstLine, delimiter);
	const normalized = headers.map((h) => h.toLowerCase());

	const problems = [];

	const emptyCount = headers.filter((h) => h === "").length;
	if (emptyCount > 0) {
		problems.push(
			`${emptyCount} header cell${emptyCount === 1 ? " is" : "s are"} empty.`
		);
	}

	const seen = new Set();
	const dupes = new Set();
	normalized.forEach((h) => {
		if (h === "") return;
		if (seen.has(h)) dupes.add(h);
		seen.add(h);
	});
	if (dupes.size > 0) {
		problems.push(`Duplicate headers: ${[...dupes].join(", ")}.`);
	}

	if (headers.length < 2) {
		problems.push(
			"Only one column detected — check that the file is comma/semicolon/tab separated."
		);
	}

	const missingRecommended = RECOMMENDED_HEADERS.filter(
		(h) => !normalized.includes(h)
	);
	const suggestions =
		problems.length === 0 && missingRecommended.length > 0
			? `Heads up: these recommended headers are missing (matching still works):\n${missingRecommended.join(
					", "
			  )}`
			: null;

	return { ok: problems.length === 0, problems, suggestions };
}
